import React, { useState } from 'react';
import { bool, func, string, object } from 'prop-types';
import join from 'url-join';

import StatusModal from '../../common/StatusModal';
import DownloadProductsModal from './DownloadProductsModal';
import http from '../../../util/http';
import { getReportStatusAndUrl } from '../actions';

import useModal from '../../../hooks/useModal';

const api2 = join(import.meta.env.VITE_API2_URL, 'v1');

const DownloadProducts = ({
  closeDownloadProductsModal,
  isDownloadProductsModalOpen,
  catalogId,
  selectedFlatRows,
  isAllRowsSelected,
  filterQueryParams,
  isCatalogsList,
  selectedProductCodes
}) => {
  const [isStatusModalOpen, openStatusModal, closeStatusModal] = useModal();
  const [isDownloading, setIsDownloading] = useState(false);
  const [statusModalProps, setStatusModalProps] = useState({ title: '' });
  const [includeImages, setIncludeImages] = useState(null);

  const successModalProps = {
    type: 'success',
    title: 'Success:',
    message: 'Your download was successful.'
  };

  const errorModalProps = {
    type: 'error',
    title: 'Error:',
    message: 'Your download was unsuccessful, please try again.'
  };

  const handleIncludeImages = e => {
    if (e.target.checked) {
      setIncludeImages(e.target.value === 'true');
    } else {
      setIncludeImages(null);
    }
  };

  const generateCatalogProductsReport = async () => {
    let path;
    const includeImagesParam = `includeImages=${includeImages ?? true}`;
    if (isCatalogsList) {
      path = `reports/generateCatalogsProductsReport?filter=catalogId(${catalogId})&${includeImagesParam}`;
    } else if (isAllRowsSelected) {
      const queryParams = filterQueryParams.replace('productCodeLike', 'productCode');
      path = `reports/generateCatalogProductsReport?filter=catalogId(${catalogId})${queryParams}&${includeImagesParam}`;
    } else {
      // eslint-disable-next-line max-len
      path = `reports/generateCatalogProductsReport?filter=catalogId(${catalogId})&filter=productCode(${selectedProductCodes})&${includeImagesParam}`;
    }

    const generateReportUrl = join(api2, path);

    try {
      const response = await http.post(generateReportUrl);
      const reportId = response.data.id;
      const downloadUrl = await getReportStatusAndUrl(reportId);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      return { ok: true };
    } catch (error) {
      return {
        ok: false,
        message: error?.message || errorModalProps.message
      };
    }
  };

  const downloadCatalogProductsReport = async () => {
    setIsDownloading(true);
    const productReportResponse = await generateCatalogProductsReport();
    if (productReportResponse.ok) {
      setStatusModalProps(successModalProps);
    } else {
      setStatusModalProps({
        ...errorModalProps,
        message: productReportResponse.message
      });
    }
    setIsDownloading(false);
    closeDownloadProductsModal();
    openStatusModal();
  };

  return (
    <>
      <DownloadProductsModal
        isOpen={isDownloadProductsModalOpen}
        closeModal={closeDownloadProductsModal}
        downloadCatalogProductsReport={downloadCatalogProductsReport}
        isDownloading={isDownloading}
        isCatalogsList={isCatalogsList}
        selectedFlatRows={selectedFlatRows}
        includeImages={includeImages}
        onIncludeImages={handleIncludeImages}
      />
      <StatusModal isOpen={isStatusModalOpen} closeModal={closeStatusModal} modalProps={statusModalProps} />
    </>
  );
};

DownloadProducts.propTypes = {
  isDownloadProductsModalOpen: bool.isRequired,
  closeDownloadProductsModal: func.isRequired,
  filterQueryParams: string.isRequired,
  catalogId: string.isRequired,
  selectedFlatRows: object.isRequired,
  isCatalogsList: bool.isRequired,
  selectedProductCodes: object.isRequired,
  isAllRowsSelected: bool
};

DownloadProducts.defaultProps = {
  isAllRowsSelected: false
};

export default DownloadProducts;
